import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useIntl } from 'react-intl';
import { Form } from 'antd';
import Icon from '@ant-design/icons';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import classnames from 'classnames';

import { ContextWrapper } from 'contexts/wrapper.context';
import {
  createNewMember,
  getUserPermission,
  updateMember
} from 'services/team-members';
import {
  DEFAULT_PERMISSION_MODULE_ACCESS,
  PERMISSIONS_MODULES_ID,
  PERMISSIONS_OPTIONS_VALUE,
  RESPONSE_ERROR_CODES
} from 'constants/team-members';
import {
  fillRestOfPermissionsBySpecificAccess,
  getFullName,
  hasNoSelectedPermissions
} from 'utils/team-members';
import { userInfo } from 'utils/helpers';
import { useModal } from 'contexts/modalProvider.context';
import { mediaHook } from 'customHooks';

import Container from 'components/Container/Container';
import Header from 'components/Header/Header';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';
import BasicInfoSection from 'components/NewTeamMembers/components/CreateEditMember/BasicInfoSection/BasicInfoSection';
import PermissionsSection from 'components/NewTeamMembers/components/CreateEditMember/PermissionsSection/PermissionsSection';
import ConfirmationModal from 'components/BRModals/ConfirmationModal/ConfirmationModal';

import { ReactComponent as CloseIcon } from 'assets/bosta-icons/Close.svg';
import { ReactComponent as LeftIcon } from 'assets/bosta-icons/Chevron-left.svg';

import './CreateEditMember.less';

const { NO_ACCESS } = PERMISSIONS_OPTIONS_VALUE;
const { REPEATED_EMAIL, REPEATED_NAME } = RESPONSE_ERROR_CODES;

const CreateEditMember = ({ mobileScreenSizes: { isMediumMobileScreen } }) => {
  const { id: memberId } = useParams();
  const { formatMessage } = useIntl();
  const formRef = useRef();
  const { setPreventNavigation } = useContext(ContextWrapper);
  const history = useHistory();
  const { openModal } = useModal();

  const [isLoading, setIsLoading] = useState(false);
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);
  const [isPrimaryBtnDisabled, setIsPrimaryBtnDisabled] = useState(true);
  const [isUsedEmail, setIsUsedEmail] = useState(false);
  const [isUsedName, setIsUsedName] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState(
    DEFAULT_PERMISSION_MODULE_ACCESS
  );
  const [fetchedMemberDetails, setFetchedMemberDetails] = useState({});

  const { businessId } = userInfo?.businessAdminInfo || {};

  const { DELIVERIES, PICKUPS, PRODUCTS, PRODUCT_CATEGORY } =
    PERMISSIONS_MODULES_ID;

  const isEdit = !!memberId;
  const createOrEditMsgId = isEdit ? 'edit_member' : 'create_member';

  const isRequiredFieldsFilled = ({ email, name }) => email && name;

  const onValuesChange = (changedValue, allValues) => {
    const { email, name } = changedValue;

    if (isString(email) && isUsedEmail) {
      setIsUsedEmail(false);
    }
    if (isString(name) && isUsedName) {
      setIsUsedName(false);
    }

    setIsPrimaryBtnDisabled(!isRequiredFieldsFilled(allValues));
    setIsFieldsTouched(true);
  };

  const goToTeamMembersList = () => {
    history.push('/team-members');
  };

  const onCancel = () => {
    if (
      isFieldsTouched ||
      (!isEdit &&
        !isEqual(selectedPermissions, DEFAULT_PERMISSION_MODULE_ACCESS))
    ) {
      setPreventNavigation(false);
      openModal(ConfirmationModal, {
        title: formatMessage({
          id: `team_members.create_edit_member.${createOrEditMsgId}.cancel_confirmation_modal.title`
        }),
        content: formatMessage({
          id: `team_members.create_edit_member.${createOrEditMsgId}.cancel_confirmation_modal.message`
        }),
        confirmButtonLabel: formatMessage({
          id: `team_members.create_edit_member.${createOrEditMsgId}.cancel_confirmation_modal.confirm_btn`
        }),
        cancelButtonLabel: formatMessage({
          id: `team_members.create_edit_member.${createOrEditMsgId}.cancel_confirmation_modal.cancel_btn`
        }),
        modalAction: goToTeamMembersList
      });
    } else {
      goToTeamMembersList();
    }
  };

  const formatPermissionsForPayload = () => {
    const permissions = fillRestOfPermissionsBySpecificAccess({
      permissions: selectedPermissions,
      specificAccess: NO_ACCESS
    });

    // Requested from BE to send PICKUPS access equal to DELIVERIES
    permissions[PICKUPS] = permissions[DELIVERIES];

    // Requested from BE to send PRODUCT_CATEGORY access equal to PRODUCTS
    permissions[PRODUCT_CATEGORY] = permissions[PRODUCTS];

    return permissions;
  };

  const onFinish = async (values = {}) => {
    setIsLoading(true);

    try {
      const payload = {
        ...values,
        sllrPermissions: formatPermissionsForPayload()
      };

      if (isEdit) {
        payload.userId = memberId;
        delete payload.email;

        if (fetchedMemberDetails.name === payload.name) {
          delete payload.name;
        }

        await updateMember(businessId, payload);
      } else {
        await createNewMember(businessId, payload);
      }

      setPreventNavigation(false);
      notify({
        msg: formatMessage({
          id: `team_members.create_edit_member.${createOrEditMsgId}.success_msg`
        }),
        type: 'success'
      });
      goToTeamMembersList();
    } catch (error) {
      const { message, errorCode } = error;
      if (REPEATED_EMAIL.includes(errorCode)) {
        setIsUsedEmail(true);
      } else if (REPEATED_NAME.includes(errorCode)) {
        setIsUsedName(true);
      } else {
        notify({ msg: message, error });
      }
    }

    setIsLoading(false);
  };

  const onConfirm = () => {
    formRef.current.validateFields().then(onFinish);
  };

  const fillMemberForm = ({ name, emails = [], sllrPermissions = {} }) => {
    formRef.current?.setFieldsValue({
      email: emails[0]?.address,
      name
    });

    setSelectedPermissions(sllrPermissions);
  };

  const getMemberDetails = async () => {
    setIsLoading(true);

    try {
      const member = (await getUserPermission(memberId)) || {};

      if (member.organizationalAccount) {
        notify({
          msg: formatMessage({
            id: 'team_members.create_edit_member.edit_member.first_admin_edit_error_msg'
          })
        });
        goToTeamMembersList();
      } else {
        member.name = getFullName(member.profile);
        setFetchedMemberDetails(member);
        fillMemberForm(member);
      }
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const headerContent = () => (
    <div
      className={classnames('br-create-edit-member__header', {
        'fixed-header': isMediumMobileScreen
      })}
    >
      {!isMediumMobileScreen && (
        <div className={'br-create-edit-member__header__title'}>
          <Icon
            component={isEdit ? LeftIcon : CloseIcon}
            onClick={onCancel}
            className="br-header__go-back-btn ant-icon-md"
          />
          <span className="display-sm">
            {formatMessage({
              id: `team_members.create_edit_member.${createOrEditMsgId}.title`
            })}
          </span>
        </div>
      )}
      <div className="br-create-edit-member__header__actions">
        <BRButton
          label={formatMessage({ id: 'common.cancel' })}
          onClick={onCancel}
        />
        <BRButton
          type="primary"
          label={formatMessage({
            id: `team_members.create_edit_member.${createOrEditMsgId}.confirm_btn`
          })}
          disabled={
            isLoading ||
            isPrimaryBtnDisabled ||
            hasNoSelectedPermissions(selectedPermissions)
          }
          onClick={onConfirm}
        />
      </div>
    </div>
  );

  const renderHeader = () => {
    return isMediumMobileScreen ? (
      <Header
        title={formatMessage({ id: 'new_header.products.title' })}
        subTitle={formatMessage({
          id: 'new_header.products.sub_title'
        })}
        hideRightSection
      />
    ) : (
      <Header headerContent={headerContent} />
    );
  };

  useEffect(() => {
    if (isEdit) {
      getMemberDetails();
    }
  }, []);

  useEffect(() => {
    if (isFieldsTouched) {
      setPreventNavigation(true);
    }
  }, [isFieldsTouched]);

  return (
    <Container
      containerClassName="br-create-edit-member"
      header={renderHeader()}
      content={
        <>
          {isMediumMobileScreen && headerContent()}
          <LoadingWrapper loading={isLoading}>
            <Form
              scrollToFirstError
              ref={formRef}
              name="create-edit-member-form"
              onValuesChange={onValuesChange}
              className="br-create-edit-member__form"
            >
              <BasicInfoSection
                formRef={formRef}
                isUsedEmail={isUsedEmail}
                isUsedName={isUsedName}
                isEdit={isEdit}
              />
              <PermissionsSection
                formRef={formRef}
                selectedPermissions={selectedPermissions}
                setSelectedPermissions={setSelectedPermissions}
                isFieldsTouched={isFieldsTouched}
                setIsFieldsTouched={setIsFieldsTouched}
                isPrimaryBtnDisabled={isPrimaryBtnDisabled}
                setIsPrimaryBtnDisabled={setIsPrimaryBtnDisabled}
                isEdit={isEdit}
              />
            </Form>
          </LoadingWrapper>
        </>
      }
    />
  );
};

export default mediaHook(CreateEditMember);
